import { LoaderFunctionArgs } from 'react-router-dom'
import { toast } from 'react-toastify'

import { eventsApi, SelectSponsorship } from '../../services/events'
import i18n from '../../i18n'
import { OptionsParticipants, Participants } from '../../types'
import type { AppStore } from '../../store'

export const putOptionParticipation =
  (store: AppStore) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const data = (await request.json()) as Pick<
      OptionsParticipants,
      'status' | 'present' | 'hotel' | 'value'
    > & { optionId: number; participantId: number }
    const { eventId, currentParticipation } = params
    const { t } = i18n

    if (currentParticipation === 'new') {
      const result = await store.dispatch(
        eventsApi.endpoints.addOptionParticipation.initiate({
          eventId: Number(eventId),
          data: {
            status: data.status,
            present: data.present,
            hotel: data.hotel,
            participant: data.participantId,
            value: data.value,
            option: Number(data.optionId),
          },
        })
      )

      if ('error' in result && result.error) {
        toast.error(t('Erreur lors de la creation de la participation'))
        return result
      }
      if ('data' in result) {
        toast.success(t('Inscription complémentaire ajoutée'))
        return result
      }
    } else {
      const result = await store.dispatch(
        eventsApi.endpoints.modifyOptionParticipation.initiate({
          eventId: Number(eventId),
          participationId: Number(currentParticipation),
          data: {
            status: data.status,
            present: data.present,
            hotel: data.hotel,
            value: data.value,
          },
        })
      )
      if ('error' in result && result.error) {
        toast.error(t('Erreur lors de la modification de la participation'))
        return result
      }
      if ('data' in result) {
        toast.success(t('Inscription complémentaire modifiée'))
        return result
      }
    }

    return new Response(null, {
      status: 400,
      statusText: 'Failed to dispatch sub event participation',
    })
  }

const isAddParticipant = (
  _data: unknown,
  currentParticipant: string | undefined
): _data is Pick<Participants, 'status' | 'id'> => currentParticipant === 'new'

const isPatchParticipant = (
  _data: unknown,
  currentParticipant: string | undefined
): _data is Pick<Participants, 'status' | 'present'> => currentParticipant !== 'new'

export const putParticipant =
  (store: AppStore) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const { eventId, currentParticipant } = params
    const data = (await request.json()) as unknown

    const { t } = i18n

    if (isAddParticipant(data, currentParticipant)) {
      const result = await store.dispatch(
        eventsApi.endpoints.addParticipant.initiate({
          eventId: Number(eventId),
          data: {
            status: data.status,
            user: data.id,
          },
        })
      )
      if ('error' in result && result.error) {
        toast.error(t('Erreur lors de la creation de la participation'))
        return result
      }
      if ('data' in result) {
        toast.success(t('Inscription ajoutée'))
        return result
      }
    } else if (isPatchParticipant(data, currentParticipant)) {
      const result = await store.dispatch(
        eventsApi.endpoints.patchParticipant.initiate({
          eventId: Number(eventId),
          id: Number(currentParticipant),
          data: {
            status: data.status,
            present: data.present,
          },
        })
      )
      if ('error' in result && result.error) {
        toast.error(t('Erreur lors de la modification de la participation'))
        return result
      }
      if ('data' in result) {
        toast.success(t('Inscription modifiée'))
        return result
      }
    }

    return new Response(null, {
      status: 400,
      statusText: 'Failed to dispatch event participation',
    })
  }

export const putSponsorship =
  (store: AppStore) =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const { eventId, sponsorshipId } = params
    const data = (await request.json()) as SelectSponsorship['data']

    const { t } = i18n

    const result = await store.dispatch(
      eventsApi.endpoints.selectSponsorship.initiate({
        eventId: Number(eventId),
        id: Number(sponsorshipId),
        data,
      })
    )

    if ('error' in result && result.error) {
      toast.error(t('Erreur lors de la modification de la prise en charge'))
      return result
    }
    if ('data' in result) {
      toast.success(t('Prise en charge modifiée'))
      return result
    }

    return new Response(null, {
      status: 400,
      statusText: 'Failed to dispatch sponsorship',
    })
  }
